import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const SUIT = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}
function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}
function findBySid(sub, sid) {
  return sub.find((s) => s.sid === sid);
}
function findByNat(sub, nat) {
  return sub.find((s) => s.nat === nat);
}
function bookOf(exposures, nat) {
  return roundBook(exposures?.[nat] ?? 0);
}
function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// Range collapse (info-circle + #demo icon-range R:min-max)
function Rng({ id, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="d-inline-block">
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={open ? 'true' : 'false'}
        className={`fas fa-info-circle${open ? '' : ' collapsed'}`}
        onClick={() => setOpen((v) => !v)}
      ></i>
      {' '}
      <div id={id} className={`icon-range collapse${open ? ' show' : ''}`}>
        {'\n                      R:'}<span>{min}</span>-<span>{max}</span>
      </div>
    </div>
  );
}

// Dus ka Dum (dum10) — table duskadum. Main: "Next Total N or More" back/lay +
// range #demo (R:100-25K) + 2 stacked book-black. Fancy (dkd-other): Even/Odd/
// Red(heart+diamond)/Black(spade+club) back-only — upar odds row, neeche bet row
// with book-black hamesha; min-max R:100-10K. Suits CDN se.
const BetTableDum10 = memo(function BetTableDum10({ gameData, onBet, exposures, mainBook }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});

  // Main market: sid 1 carries the WHOLE market — back price in `b`, lay
  // price in `l`, one gstatus. sid 2 duplicates the nat but is permanently
  // SUSPENDED with 0/0, so driving the lay box off it kept it locked forever
  // (same fix as the player BetTableDum10).
  const mainBack = findBySid(sub, 1);
  const mainNat = mainBack?.nat || 'Next Total 100 or More';
  const laySusp = isSusp(mainBack) || !Number(mainBack?.l);
  // Top = if the total lands, bottom = if it doesn't (computeDum10MainBook).
  const bookMain1 = roundBook(mainBook?.lands ?? 0);
  const bookMain2 = roundBook(mainBook?.misses ?? 0);

  const even = findByNat(sub, 'Even');
  const odd = findByNat(sub, 'Odd');
  const red = findByNat(sub, 'Red');
  const black = findByNat(sub, 'Black');
  const bookEven = bookOf(exposures, 'Even');
  const bookOdd = bookOf(exposures, 'Odd');
  const bookRed = bookOf(exposures, 'Red');
  const bookBlack = bookOf(exposures, 'Black');

  return (
    <>
      {/* ── Main Market ── */}
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <div className="p-relative">
            <b>{mainNat}</b>
            {' '}
            <Rng id="demo" min="100" max="25K" />
          </div>
          {' '}
          <div className="float-right mr-2">
            <span className="d-block"><span className={`casino-book ${bookCls(bookMain1)}`}>{fmtBook(bookMain1)}</span></span>
            {' '}
            <span className="d-block"><span className={`casino-book ${bookCls(bookMain2)}`}>{fmtBook(bookMain2)}</span></span>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${suspCls(mainBack)}`}
            onClick={isSusp(mainBack) ? undefined : () => handleBet(mainBack.sid, 'back', mainBack.b, mainBack)}
          >
            <span className="casino-box-odd">{mainBack?.b || 0}</span>
          </div>
          {' '}
          <div
            className={`lay casino-bl-box-item${laySusp ? ' suspended' : ''}`}
            onClick={laySusp ? undefined : () => handleBet(mainBack.sid, 'lay', mainBack.l, mainBack)}
          >
            <span className="casino-box-odd">{mainBack?.l || 0}</span>
          </div>
        </div>
      </div>

      {/* ── Fancy Markets (Even / Odd / Red / Black) ── */}
      <div className="dkd-other mt-2">
        {/* odds row */}
        <div className="casino-box-row">
          <div className="casino-bl-box"><b>{even?.b || 0}</b></div>
          {' '}
          <div className="casino-bl-box"><b>{odd?.b || 0}</b></div>
          {' '}
          <div className="casino-bl-box"><b>{red?.b || 0}</b></div>
          {' '}
          <div className="casino-bl-box"><b>{black?.b || 0}</b></div>
        </div>
        {' '}
        {/* bet row */}
        <div className="casino-box-row">
          <div className="casino-bl-box">
            <div
              className={`back casino-bl-box-item${bookEven ? ' lock-top' : ''}${suspCls(even)}`}
              onClick={isSusp(even) ? undefined : () => handleBet(even.sid, 'back', even.b, even)}
            >
              <span className="casino-box-odd">Even</span>
              {' '}
              <span className={`casino-book ${bookCls(bookEven)}`}>{fmtBook(bookEven)}</span>
            </div>
          </div>
          {' '}
          <div className="casino-bl-box">
            <div
              className={`back casino-bl-box-item${bookOdd ? ' lock-top' : ''}${suspCls(odd)}`}
              onClick={isSusp(odd) ? undefined : () => handleBet(odd.sid, 'back', odd.b, odd)}
            >
              <span className="casino-box-odd">Odd</span>
              {' '}
              <span className={`casino-book ${bookCls(bookOdd)}`}>{fmtBook(bookOdd)}</span>
            </div>
          </div>
          {' '}
          <div className="casino-bl-box">
            <div
              className={`back casino-bl-box-item casino-card-img${bookRed ? ' lock-top' : ''}${suspCls(red)}`}
              onClick={isSusp(red) ? undefined : () => handleBet(red.sid, 'back', red.b, red)}
            >
              <span>
                <img src={`${SUIT}/heart.png`} />
                {' '}
                <img src={`${SUIT}/diamond.png`} />
              </span>
              {' '}
              <span className={`casino-book ${bookCls(bookRed)}`}>{fmtBook(bookRed)}</span>
            </div>
          </div>
          {' '}
          <div className="casino-bl-box">
            <div
              className={`back casino-bl-box-item casino-card-img${bookBlack ? ' lock-top' : ''}${suspCls(black)}`}
              onClick={isSusp(black) ? undefined : () => handleBet(black.sid, 'back', black.b, black)}
            >
              <span>
                <img src={`${SUIT}/spade.png`} />
                {' '}
                <img src={`${SUIT}/club.png`} />
              </span>
              {' '}
              <span className={`casino-book ${bookCls(bookBlack)}`}>{fmtBook(bookBlack)}</span>
            </div>
          </div>
        </div>
        {' '}
        <div className="text-center w-100">
          <span className="float-right casino-min-max">R:<span>100</span>-<span>10K</span></span>
        </div>
      </div>
    </>
  );
});

export default BetTableDum10;
